import styled from 'styled-components';
import COLORS from '../../../style/universal.color';

const { mlbWhite, mlbGrey2, mlbGrey3, mlbGrey4, mlbBlack, focusBlue } = COLORS;

interface StyledCellProps {
  colgroupStart?: boolean;
  colgroupEnd?: boolean;
  alignment: string;
  fixedWidth?: string;
  colIndex: number;
  lastColumn: boolean;
  lastRow: boolean;
  firstRow: boolean;
  firstGroupStart?: boolean;
  dark?: boolean;
  columnHighlighted?: boolean;
  cellHighlighted?: boolean;
  isSelected?: boolean;
  highlightHead: boolean;
  highlightColor?: string;
}

interface StyledHeadProps {
  alignment: string;
  fixedWidth?: string;
  firstColPinned?: boolean;
  lastColPinned?: boolean;
  inHead: boolean;
  dark?: boolean;
  columnHighlighted?: boolean;
  normalHeader: boolean;
  isSelected?: boolean;
  sortable?: boolean;
  highlightColor?: string;
}

interface SortIndicatorProps {
  isSelected: boolean;
  isDescending?: boolean;
}

export const SortIndicator = styled.div<SortIndicatorProps>`
  display: inline-block;
  width: 7px;
  height: 7px;
  position: relative;
  svg {
    opacity: ${({ isSelected }) => (isSelected ? 1 : 0)};
    width: 7px;
    height: 7px;
    left: -5px;
    position: absolute;
    &:first-child {
      top: -4px;
    }
    &:last-child {
      bottom: -2px;
    }
    transition-duration: 0.2s;
  }
`;

const buildBorder = (before: boolean, backgroundColor: string): string => `
  &:${before ? 'before' : 'after'}{
    content: '';
    position: absolute;
    ${before ? 'left: 0px;' : 'right: 0px;'}
    top: 0px;
    height: 39px;
    width: 1px;
    display: inline;
    background-color: ${backgroundColor};
  }
`;

export const SkeletonWrapper = styled.span`
  display: inline-block;
`;

export const StyledTableCell = styled.td<StyledCellProps>`
  font-size: 13px;
  font-weight: 500;
  text-align: ${({ alignment }) => alignment};
  width: ${({ fixedWidth }) => fixedWidth};
  position: relative;
  white-space: nowrap;
  color: ${({ dark }) => (dark ? mlbGrey2 : mlbBlack)};
  z-index: 0;
  box-sizing: border-box;

  ${({ columnHighlighted, cellHighlighted, highlightColor }) =>
    !columnHighlighted && cellHighlighted
      ? `
    background-color: ${highlightColor};
  `
      : ''}

  ${({ columnHighlighted, lastRow, firstRow, highlightColor, highlightHead }) =>
    columnHighlighted
      ? `
    background-color: ${highlightColor};
    ${
      firstRow && !highlightHead
        ? `
        border-top-right-radius: 4px;
        border-top-left-radius: 4px;
      `
        : ''
    }
    ${
      lastRow
        ? `
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
      `
        : ''
    }
  `
      : ''}

  ${({ colgroupStart, firstGroupStart, colIndex, dark }) =>
    colgroupStart && firstGroupStart && colIndex > 0 ? buildBorder(true, dark ? mlbGrey4 : mlbGrey2) : ''}

  ${({ colgroupEnd, lastColumn, dark }) =>
    colgroupEnd && !lastColumn ? buildBorder(false, dark ? mlbGrey4 : mlbGrey2) : ''}
`;

export const StyledTableHead = styled.th<StyledHeadProps>`
  font-size: ${({ inHead }) => (!inHead ? '13px' : '11px')};
  font-weight: ${({ inHead }) => (!inHead ? '400' : '600')};
  color: ${({ dark, inHead }) => (dark ? (!inHead ? mlbGrey2 : mlbGrey3) : !inHead ? mlbBlack : mlbGrey4)};

  text-align: ${({ alignment }) => alignment};
  width: ${({ fixedWidth }) => fixedWidth};
  position: relative;
  white-space: nowrap;
  box-sizing: border-box;

  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &:hover,
  &:focus {
    ${SortIndicator} {
      svg {
        opacity: 1;
      }
    }
  }

  ${({ sortable }) =>
    sortable
      ? `
    cursor: pointer;
    &:focus {
      outline: ${focusBlue} auto 1px;
    }
  `
      : ''}

  ${({ columnHighlighted, dark, highlightColor }) =>
    columnHighlighted
      ? `
    background-color: ${highlightColor};
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
    color: ${dark ? mlbWhite : mlbBlack};
    &:after {
      content: '';
      bottom: -6px;
      left: 0px;
      display: block;
      position: absolute;
      height: 5px;
      background-color: ${highlightColor};
      width: 100%;
    }
  `
      : ''}

  ${({ normalHeader, dark }) =>
    normalHeader
      ? `
        border-bottom: 1px solid ${dark ? mlbGrey4 : mlbGrey2};
      `
      : ''}

  ${({ firstColPinned }) =>
    firstColPinned
      ? `
      position: absolute;
      left: 0;
      z-index: 1;
  `
      : ''}

  ${({ lastColPinned }) =>
    lastColPinned
      ? `
      position: absolute;
      right: 0;
      z-index: 1;
  `
      : ''}
`;
